import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';

const DOCUMENT_TYPES = [
  'Offer Letter',
  "Guarantor's Undertaking Form",
  'Letter of Appointment and Employment Agreement',
  'Non-Disclosure and Confidentiality Agreement',
  'Intellectual Property and Work Product Assignment Agreement',
  'Non-Solicitation and Limited Non-Compete Agreement',
  'Conflict of Interest, Gifts and Outside Activities Declaration',
  'Company Property and Asset Agreement',
  'Data Protection and Privacy Agreement',
  'IT, Systems and Digital Access Agreement',
  'Health and Safety Acknowledgment',
  'The Bloans Standard and Policy Acknowledgment',
  'Exit Clearance and Continuing Obligations Form',
  'Onboarding Document Checklist',
];

const STATUS_COLORS = {
  draft:{ bg:'#F5F5F5', color:'#8A8A8A', label:'Draft' },
  under_review:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Under Review' },
  approved:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Approved' },
  active:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Active' },
  retired:{ bg:'#FDEAEA', color:'#8F2E24', label:'Retired' },
  archived:{ bg:'#F0F0F0', color:'#666', label:'Archived' },
};

const MERGE_FIELDS = [
  { key:'{{prospect_full_name}}', label:'Prospect Full Name' },
  { key:'{{prospect_first_name}}', label:'Prospect First Name' },
  { key:'{{prospect_email}}', label:'Prospect Email' },
  { key:'{{prospect_phone}}', label:'Prospect Phone' },
  { key:'{{prospect_ref}}', label:'Prospect Reference' },
  { key:'{{job_title}}', label:'Job Title / Position' },
  { key:'{{department_name}}', label:'Department' },
  { key:'{{reports_to}}', label:'Reports To' },
  { key:'{{gross_monthly_salary}}', label:'Gross Monthly Salary' },
  { key:'{{proposed_start_date}}', label:'Proposed Start Date' },
  { key:'{{probation_period}}', label:'Probation Period' },
  { key:'{{offer_expiry_date}}', label:'Offer Expiry Date' },
  { key:'{{work_location}}', label:'Work Location' },
  { key:'{{employment_category}}', label:'Employment Type' },
  { key:'{{guarantor_full_name}}', label:'Guarantor Full Name' },
  { key:'{{guarantor_email}}', label:'Guarantor Email' },
  { key:'{{guarantor_phone}}', label:'Guarantor Phone' },
  { key:'{{guarantor_relationship}}', label:'Guarantor Relationship' },
  { key:'{{guarantor_occupation}}', label:'Guarantor Occupation' },
  { key:'{{guarantor_address}}', label:'Guarantor Address' },
  { key:'{{authorised_signatory_name}}', label:'Authorised Signatory' },
  { key:'{{company_name}}', label:'Company Name' },
  { key:'{{company_address}}', label:'Company Address' },
  { key:'{{today_date}}', label:"Today's Date" },
  { key:'{{document_ref}}', label:'Document Reference' },
];

export default function DocumentTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [form, setForm] = useState({
    title:'', document_type:'', content:'',
    is_mandatory:false, requires_company_signature:false,
    requires_prospect_signature:true,
    applicable_roles:[], applicable_employment_types:[],
    change_notes:'', effective_date:''
  });

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    get('/document-templates').then(d => setTemplates(d.data||[])).finally(() => setLoading(false));
  };

  const loadSelected = (id) => {
    get('/document-templates/'+id).then(d => setSelected(d));
  };

  const save = async () => {
    if (!form.title || !form.document_type) { alert('Title and document type required'); return; }
    setSaving(true);
    try {
      if (selected?.data?.id) {
        await put('/document-templates/'+selected.data.id, form);
      } else {
        await post('/document-templates', form);
      }
      setShowForm(false);
      setSelected(null);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    const reason = status === 'retired' ? window.prompt('Reason for retiring:') : null;
    if (status === 'retired' && !reason) return;
    try {
      await put('/document-templates/'+id+'/status', { status, change_notes: reason });
      load();
      if (selected?.data?.id === id) loadSelected(id);
    } catch(err) { alert(err.message); }
  };

  const previewTemplate = async (id) => {
    try {
      const result = await post('/document-templates/'+id+'/preview', {});
      setPreview(result.preview);
      setShowPreview(true);
    } catch(err) { alert(err.message); }
  };

  const insertMergeField = (field) => {
    setForm({...form, content: form.content + field});
  };

  const filtered = activeTab === 'all' ? templates :
    templates.filter(t => t.status === activeTab);

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:"Cormorant Garamond,serif",fontSize:"1.8rem",color:"#2C1F16",margin:"0 0 4px"}}>
            Document Templates
          </h2>
          <p style={{color:"#8A8A8A",fontSize:13,margin:0}}>
            Manage approved document templates for onboarding and HR compliance
          </p>
        </div>
        <div style={{display:"flex",gap:8}}>
          {selected && (
            <button className="btn btn-ghost" onClick={()=>setSelected(null)}>← All Templates</button>
          )}
          {!selected && (
            <button className="btn btn-primary btn-sm" onClick={()=>{
              setForm({title:"",document_type:"",content:"",is_mandatory:false,
                requires_company_signature:false,requires_prospect_signature:true,
                applicable_roles:[],applicable_employment_types:[],change_notes:"",effective_date:""});
              setShowForm(true);
            }}>+ New Template</button>
          )}
        </div>
      </div>

      {!selected ? (
        <>
          {/* Status filter tabs */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
            {[["all","All"],["draft","Draft"],["active","Active"],["approved","Approved"],
              ["under_review","Under Review"],["retired","Retired"]].map(([key,label])=>(
              <button key={key} className={"btn btn-sm "+(activeTab===key?"btn-primary":"btn-ghost")}
                onClick={()=>setActiveTab(key)}>
                {label} ({key==="all"?templates.length:templates.filter(t=>t.status===key).length})
              </button>
            ))}
          </div>

          {loading ? <div className="spinner"/> : filtered.length === 0 ? (
            <div className="empty-state">
              <h3>No templates yet</h3>
              <p>Click + New Template to create your first document template</p>
            </div>
          ) : (
            <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:12,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#2C1F16"}}>
                    {["Title","Type","Version","Status","Mandatory","Signatures",""].map(h=>(
                      <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:9,
                        color:"#E8C466",textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t,i)=>{
                    const st = STATUS_COLORS[t.status]||STATUS_COLORS.draft;
                    return (
                      <tr key={t.id} style={{background:i%2===0?"#fff":"#F9F8F5",cursor:"pointer"}}
                        onClick={()=>loadSelected(t.id)}>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8",fontWeight:600,color:"#2C1F16"}}>{t.title}</td>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8",color:"#555",fontSize:11}}>{t.document_type}</td>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8",textAlign:"center"}}>v{t.version}</td>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8"}}>
                          <span style={{fontSize:9,padding:"3px 10px",borderRadius:20,fontWeight:600,
                            background:st.bg,color:st.color}}>{st.label}</span>
                        </td>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8",textAlign:"center"}}>
                          {t.is_mandatory ? "✅" : "—"}
                        </td>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8",fontSize:11,color:"#8A8A8A"}}>
                          {[t.requires_prospect_signature&&"Prospect",t.requires_company_signature&&"Company"]
                            .filter(Boolean).join(", ")||"None"}
                        </td>
                        <td style={{padding:"10px 14px",borderBottom:"1px solid #EDE7D8",color:"#A8893D"}}>→</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        // Detail view
        <div>
          {/* Template header */}
          <div style={{background:"#3B2A21",borderRadius:12,padding:20,marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{color:"#A8893D",fontSize:11,marginBottom:4,textTransform:"uppercase",letterSpacing:".08em"}}>
                  {selected.data?.document_type} · v{selected.data?.version}
                </div>
                <div style={{color:"#F2EFE7",fontFamily:"Cormorant Garamond,serif",fontSize:"1.4rem",fontWeight:600}}>
                  {selected.data?.title}
                </div>
                <div style={{color:"rgba(242,239,231,.5)",fontSize:12,marginTop:4}}>
                  Created by {selected.data?.created_by_name}
                  {selected.data?.approved_by_name && ` · Approved by ${selected.data.approved_by_name}`}
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                <span style={{fontSize:10,padding:"4px 12px",borderRadius:20,fontWeight:700,
                  background:(STATUS_COLORS[selected.data?.status]||STATUS_COLORS.draft).bg,
                  color:(STATUS_COLORS[selected.data?.status]||STATUS_COLORS.draft).color}}>
                  {(STATUS_COLORS[selected.data?.status]||STATUS_COLORS.draft).label}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
            <button className="btn btn-outline btn-sm" onClick={()=>previewTemplate(selected.data.id)}>
              👁 Preview
            </button>
            <button className="btn btn-outline btn-sm" onClick={()=>{
              setForm({
                title:selected.data.title,
                document_type:selected.data.document_type,
                content:selected.data.content||"",
                is_mandatory:selected.data.is_mandatory,
                requires_company_signature:selected.data.requires_company_signature,
                requires_prospect_signature:selected.data.requires_prospect_signature,
                applicable_roles:selected.data.applicable_roles||[],
                applicable_employment_types:selected.data.applicable_employment_types||[],
                change_notes:"",
                effective_date:selected.data.effective_date||""
              });
              setShowForm(true);
            }}>✏️ Edit</button>
            {selected.data?.status === "draft" && (
              <button className="btn btn-outline btn-sm" style={{color:"#3B5BDB",borderColor:"#3B5BDB"}}
                onClick={()=>updateStatus(selected.data.id,"under_review")}>Submit for Review</button>
            )}
            {selected.data?.status === "under_review" && (
              <button className="btn btn-outline btn-sm" style={{color:"#8B5E0A",borderColor:"#8B5E0A"}}
                onClick={()=>updateStatus(selected.data.id,"approved")}>Approve</button>
            )}
            {selected.data?.status === "approved" && (
              <button className="btn btn-primary btn-sm"
                onClick={()=>updateStatus(selected.data.id,"active")}>Activate</button>
            )}
            {selected.data?.status === "active" && (
              <button className="btn btn-outline btn-sm" style={{color:"#8F2E24",borderColor:"#8F2E24"}}
                onClick={()=>updateStatus(selected.data.id,"retired")}>Retire</button>
            )}
          </div>

          {/* Template details */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10,marginBottom:20}}>
            {[
              ["Document Type", selected.data?.document_type],
              ["Version", "v"+selected.data?.version],
              ["Mandatory", selected.data?.is_mandatory?"Yes":"No"],
              ["Prospect Signature", selected.data?.requires_prospect_signature?"Required":"Not Required"],
              ["Company Signature", selected.data?.requires_company_signature?"Required":"Not Required"],
              ["Effective Date", selected.data?.effective_date||"—"],
            ].map(([label,val])=>(
              <div key={label} style={{background:"#F9F8F5",borderRadius:8,padding:12}}>
                <div style={{fontSize:9,color:"#8A8A8A",textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>{label}</div>
                <div style={{fontSize:12,fontWeight:500,color:"#2C1F16"}}>{val}</div>
              </div>
            ))}
          </div>

          {/* Content preview */}
          <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:12,padding:20,marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:"#556B2F",textTransform:"uppercase",
              letterSpacing:".08em",marginBottom:12}}>Document Content</div>
            {selected.data?.content ? (
              <div style={{fontSize:13,color:"#555",lineHeight:1.7,maxHeight:300,overflowY:"auto",
                fontFamily:"Georgia,serif",whiteSpace:"pre-wrap"}}>
                {selected.data.content.replace(/<[^>]+>/g," ").substring(0,500)}
                {selected.data.content.length > 500 && "... (click Preview to see full document)"}
              </div>
            ) : (
              <div style={{color:"#8A8A8A",fontSize:13}}>No content added yet. Click Edit to add document content.</div>
            )}
          </div>

          {/* Version history */}
          {selected.versions?.length > 0 && (
            <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:12,padding:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#556B2F",textTransform:"uppercase",
                letterSpacing:".08em",marginBottom:12}}>Version History</div>
              {selected.versions.map(v=>(
                <div key={v.id} style={{padding:"10px 0",borderBottom:"1px solid #F2EFE7",
                  display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <span style={{fontWeight:600,color:"#2C1F16",marginRight:12}}>v{v.version}</span>
                    <span style={{fontSize:12,color:"#8A8A8A"}}>{v.change_notes||"No notes"}</span>
                  </div>
                  <div style={{fontSize:11,color:"#8A8A8A"}}>
                    {v.created_by_name} · {new Date(v.created_at).toLocaleDateString("en-NG")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:800,width:"95vw"}}>
            <div className="modal-hdr">
              <span className="modal-title">{selected?.data?.id?"Edit Template":"New Template"}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:"80vh",overflowY:"auto"}}>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Template Title *</label>
                  <input className="form-control" value={form.title}
                    onChange={e=>setForm({...form,title:e.target.value})}
                    placeholder="e.g. Standard Offer Letter — Full-time" />
                </div>
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Document Type *</label>
                  <select className="form-control" value={form.document_type}
                    onChange={e=>setForm({...form,document_type:e.target.value})}>
                    <option value="">Select type...</option>
                    {DOCUMENT_TYPES.map(t=>(<option key={t} value={t}>{t}</option>))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Effective Date</label>
                  <input type="date" className="form-control" value={form.effective_date}
                    onChange={e=>setForm({...form,effective_date:e.target.value})} />
                </div>
                <div className="form-group" style={{display:"flex",alignItems:"center",gap:8,paddingTop:24}}>
                  <input type="checkbox" id="mandatory" checked={form.is_mandatory}
                    onChange={e=>setForm({...form,is_mandatory:e.target.checked})}
                    style={{accentColor:"#556B2F",width:16,height:16}} />
                  <label htmlFor="mandatory" style={{fontSize:13,cursor:"pointer"}}>Mandatory document</label>
                </div>
                <div className="form-group" style={{display:"flex",alignItems:"center",gap:8,paddingTop:24}}>
                  <input type="checkbox" id="cosign" checked={form.requires_company_signature}
                    onChange={e=>setForm({...form,requires_company_signature:e.target.checked})}
                    style={{accentColor:"#556B2F",width:16,height:16}} />
                  <label htmlFor="cosign" style={{fontSize:13,cursor:"pointer"}}>Company signature required</label>
                </div>
              </div>

              {/* Merge fields reference */}
              <div style={{background:"#F9F8F5",borderRadius:10,padding:14,marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#556B2F",textTransform:"uppercase",
                  letterSpacing:".08em",marginBottom:10}}>Available Merge Fields — click to insert</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {MERGE_FIELDS.map(f=>(
                    <button key={f.key} type="button"
                      onClick={()=>insertMergeField(f.key)}
                      style={{fontSize:10,padding:"4px 10px",borderRadius:20,border:"1px solid #A8893D",
                        background:"#fff",color:"#A8893D",cursor:"pointer",fontFamily:"monospace"}}>
                      {f.key}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Document Content (HTML supported)</label>
                <textarea className="form-control" rows={20} value={form.content}
                  onChange={e=>setForm({...form,content:e.target.value})}
                  style={{fontFamily:"Georgia,serif",fontSize:13,lineHeight:1.7}}
                  placeholder="Paste or type your document content here. Use merge fields from above to insert dynamic data.

Example:
Dear {{prospect_full_name}},

We are pleased to offer you the position of {{job_title}} at {{company_name}}...
" />
              </div>

              <div className="form-group">
                <label className="form-label">Change Notes</label>
                <input className="form-control" value={form.change_notes}
                  onChange={e=>setForm({...form,change_notes:e.target.value})}
                  placeholder="Describe what changed in this version..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?"Saving...":"Save Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {showPreview && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:800,width:"95vw"}}>
            <div className="modal-hdr">
              <span className="modal-title">Document Preview</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPreview(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:"80vh",overflowY:"auto"}}>
              <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,padding:32,
                fontFamily:"Georgia,serif",fontSize:14,lineHeight:1.9,color:"#2C1F16"}}
                dangerouslySetInnerHTML={{__html: preview}} />
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPreview(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
